interface Booking {
  id: string;
  room_id: string;
  guest_display_name: string;
  arrival_date: string;
  arrival_time: string;
  departure_date: string;
  departure_time: string;
  adults: number;
  children: number;
  infants: number;
  booking_status: string;
  beds24_status: string;
  operational_status: string;
  booking_channel: string;
  source_override: string | null;
  booking_origin: string;
  actual_check_in_at: string | null;
  actual_check_out_at: string | null;
  rooms: { name: string; room_type: string } | null;
  beds24_booking_financials: { collection_method:string; needs_review:boolean; outstanding_yen:number; invoice_credit_yen:number }[] | null;
}

interface BookingTableProps {
  bookings: Booking[];
  operationStatus: Record<string, string>;
  today: string;
  toJSTDate: (d: string) => string;
  formatDate: (d: string) => string;
  formatDatetime: (d: string) => string;
  translateChannel: (ch: string) => string;
  translateStatus: (st: string) => string;
  onCheckIn: (b: Booking) => void;
  onCheckOut: (b: Booking) => void;
  onOverrideSource: (b: Booking) => void;
  onNoShow: (b: Booking) => void;
  onCollectionMethod: (b: Booking, method:string) => void;
}

function statusBadge(status: string, label: string) {
  const colorMap: Record<string, string> = {
    expected: 'bg-accent-100 text-accent-800',
    checked_in: 'bg-emerald-100 text-emerald-700',
    checked_out: 'bg-foreground-100 text-foreground-600',
    no_show: 'bg-red-100 text-red-700',
    cancelled: 'bg-foreground-100 text-foreground-500 line-through',
    confirmed: 'bg-emerald-100 text-emerald-700',
    new: 'bg-secondary-100 text-secondary-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${colorMap[status] || 'bg-foreground-100 text-foreground-600'}`}>
      {label}
    </span>
  );
}

export default function BookingTable({
  bookings, operationStatus, today, toJSTDate,
  formatDate, formatDatetime, translateChannel, translateStatus,
  onCheckIn, onCheckOut, onOverrideSource, onNoShow, onCollectionMethod,
}: BookingTableProps) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-background-200/70">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-100">
              <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">ゲスト</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">部屋</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">到着</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">出発</th>
              <th className="text-center px-3 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">人数</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">経路</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">状態</th>
              <th className="text-right px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap text-xs">操作</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => {
              const isLoading = operationStatus[b.id] === 'loading';
              const roomName = b.rooms?.name || '—';
              const displayChannel = b.source_override || b.booking_channel;
              const isCancelled = b.booking_status === 'cancelled' || b.operational_status === 'cancelled';
              const isCheckedIn = b.operational_status === 'checked_in';
              const isCheckedOut = b.operational_status === 'checked_out';
              const isNoShow = b.operational_status === 'no_show';
              const isExpected = b.operational_status === 'expected' && !isCancelled;
              const arrivalJST = toJSTDate(b.arrival_date);
              const financial = b.beds24_booking_financials?.[0];

              const canCheckIn = isExpected;
              const canCheckOut = isCheckedIn;
              const canNoShow = isExpected && arrivalJST <= today;
              const showCheckInTime = b.actual_check_in_at;
              const showCheckOutTime = b.actual_check_out_at;

              return (
                <tr
                  key={b.id}
                  className={`border-t border-background-200/70 transition-colors ${
                    isCancelled ? 'opacity-50 bg-background-50/50' : 'hover:bg-background-100/50'
                  } ${isLoading ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground-900 whitespace-nowrap">{b.guest_display_name}</p>
                    <p className="text-[10px] text-foreground-400">{b.booking_origin === 'beds24' ? 'Beds24' : '手動'}</p>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-foreground-700">{roomName}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-foreground-700">{formatDate(b.arrival_date)}</p>
                    <p className="text-[10px] text-foreground-400">{b.arrival_time?.slice(0, 5) || '—'}</p>
                    {showCheckInTime && (
                      <p className="text-[10px] text-emerald-600">{formatDatetime(showCheckInTime)}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-foreground-700">{formatDate(b.departure_date)}</p>
                    <p className="text-[10px] text-foreground-400">{b.departure_time?.slice(0, 5) || '—'}</p>
                    {showCheckOutTime && (
                      <p className="text-[10px] text-emerald-600">{formatDatetime(showCheckOutTime)}</p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center whitespace-nowrap text-foreground-700">
                    {b.adults}{b.children > 0 && `+${b.children}`}{b.infants > 0 && `+${b.infants}`}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-foreground-700 text-xs">{translateChannel(displayChannel)}</p>
                    {b.source_override && (
                      <p className="text-[10px] text-accent-600">訂正済</p>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {statusBadge(b.operational_status, translateStatus(b.operational_status))}
                      {b.beds24_status && b.booking_origin === 'beds24' && (
                        <span className="text-[10px] text-foreground-400">Beds24: {b.beds24_status}</span>
                      )}
                    </div>
                    {financial&&<select value={financial.collection_method} onChange={e=>onCollectionMethod(b,e.target.value)} disabled={isLoading} className={`mt-1 max-w-[125px] rounded border px-1.5 py-1 text-[10px] ${financial.needs_review?'border-amber-400 bg-amber-50':'border-background-200 bg-white'}`}><option value="needs_review">回収要確認</option><option value="ota_receivable">OTA回収</option><option value="onsite">現地払い</option><option value="paid">入金済み</option></select>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {canCheckIn && (
                        <button
                          onClick={() => onCheckIn(b)}
                          disabled={isLoading}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-login-box-line"></i>
                          </span>
                          IN
                        </button>
                      )}
                      {canCheckOut && (
                        <button
                          onClick={() => onCheckOut(b)}
                          disabled={isLoading}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-logout-box-line"></i>
                          </span>
                          OUT
                        </button>
                      )}
                      {canNoShow && (
                        <button
                          onClick={() => onNoShow(b)}
                          disabled={isLoading}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-user-unfollow-line"></i>
                          </span>
                          NS
                        </button>
                      )}
                      <button
                        onClick={() => onOverrideSource(b)}
                        disabled={isLoading || isCancelled}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-secondary-50 hover:bg-secondary-100 text-secondary-700 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-edit-line"></i>
                        </span>
                        経路
                      </button>
                      {isLoading && (
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-loader-4-line text-sm text-primary-500 animate-spin"></i>
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {bookings.map((b) => {
          const isLoading = operationStatus[b.id] === 'loading';
          const roomName = b.rooms?.name || '—';
          const displayChannel = b.source_override || b.booking_channel;
          const isCancelled = b.booking_status === 'cancelled' || b.operational_status === 'cancelled';
          const isCheckedIn = b.operational_status === 'checked_in';
          const isExpected = b.operational_status === 'expected' && !isCancelled;
          const arrivalJST = toJSTDate(b.arrival_date);
          const canCheckIn = isExpected;
          const canCheckOut = isCheckedIn;
          const canNoShow = isExpected && arrivalJST <= today;
          const financial = b.beds24_booking_financials?.[0];

          return (
            <div
              key={b.id}
              className={`bg-background-50 rounded-xl border border-background-200/70 p-4 space-y-3 ${
                isCancelled ? 'opacity-50' : ''
              } ${isLoading ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-foreground-900">{b.guest_display_name}</p>
                  <p className="text-xs text-foreground-400">{b.booking_origin === 'beds24' ? 'Beds24' : '手動予約'} · {roomName}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {statusBadge(b.operational_status, translateStatus(b.operational_status))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-background-100 rounded-lg p-2">
                  <p className="text-foreground-400 mb-0.5">到着</p>
                  <p className="font-medium text-foreground-800">{formatDate(b.arrival_date)}</p>
                  <p className="text-[10px] text-foreground-400">{b.arrival_time?.slice(0, 5) || '—'}</p>
                  {b.actual_check_in_at && (
                    <p className="text-[10px] text-emerald-600">{formatDatetime(b.actual_check_in_at)}</p>
                  )}
                </div>
                <div className="bg-background-100 rounded-lg p-2">
                  <p className="text-foreground-400 mb-0.5">出発</p>
                  <p className="font-medium text-foreground-800">{formatDate(b.departure_date)}</p>
                  <p className="text-[10px] text-foreground-400">{b.departure_time?.slice(0, 5) || '—'}</p>
                  {b.actual_check_out_at && (
                    <p className="text-[10px] text-emerald-600">{formatDatetime(b.actual_check_out_at)}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <span className="bg-background-100 px-2 py-0.5 rounded-full">大人 {b.adults}{b.children > 0 && ` 子 ${b.children}`}{b.infants > 0 && ` 幼児 ${b.infants}`}</span>
                <span className="bg-background-100 px-2 py-0.5 rounded-full">{translateChannel(displayChannel)}</span>
                {b.source_override && (
                  <span className="bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full text-[10px]">訂正済</span>
                )}
              </div>

              {b.beds24_status && b.booking_origin === 'beds24' && (
                <p className="text-[10px] text-foreground-400">Beds24: {b.beds24_status}</p>
              )}
              {financial&&<label className="block text-xs text-foreground-500">回収方法<select value={financial.collection_method} onChange={e=>onCollectionMethod(b,e.target.value)} disabled={isLoading} className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${financial.needs_review?'border-amber-400 bg-amber-50':'border-background-200 bg-white'}`}><option value="needs_review">回収要確認</option><option value="ota_receivable">OTA回収</option><option value="onsite">現地払い</option><option value="paid">入金済み</option></select></label>}

              <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                {canCheckIn && (
                  <button
                    onClick={() => onCheckIn(b)}
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-login-box-line"></i>
                    </span>
                    チェックイン
                  </button>
                )}
                {canCheckOut && (
                  <button
                    onClick={() => onCheckOut(b)}
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-logout-box-line"></i>
                    </span>
                    チェックアウト
                  </button>
                )}
                {canNoShow && (
                  <button
                    onClick={() => onNoShow(b)}
                    disabled={isLoading}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-user-unfollow-line"></i>
                    </span>
                    No-show
                  </button>
                )}
                <button
                  onClick={() => onOverrideSource(b)}
                  disabled={isLoading || isCancelled}
                  className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-secondary-50 hover:bg-secondary-100 text-secondary-700 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-edit-line"></i>
                  </span>
                  経路訂正
                </button>
                {isLoading && (
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-loader-4-line text-primary-500 animate-spin"></i>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
